import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import TimeWheelColumn, { COLUMN_SIZE_CONFIG } from "./time-wheel-column";

const DEBOUNCE_MS = 120;

const SUPPORTS_SCROLLEND = typeof window !== "undefined" && "onscrollend" in window;

function useWheelScrollController({ colRef, options, setIndex, commit, valueFromScroll, settleToIndex }) {
  const timerRef = useRef(null);

  const settle = useCallback(
    (el) => {
      const { idx: finalIdx } = valueFromScroll(el, options);
      settleToIndex(el, finalIdx);
      setIndex(finalIdx);
      commit(finalIdx);
    },
    [options, setIndex, commit, valueFromScroll, settleToIndex]
  );

  useEffect(() => {
    if (!SUPPORTS_SCROLLEND) return;
    const el = colRef.current;
    if (!el) return;
    const handleEnd = () => settle(el);
    el.addEventListener("scrollend", handleEnd);
    return () => el.removeEventListener("scrollend", handleEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settle]);

  const onScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const { idx } = valueFromScroll(el, options);
      setIndex(idx);

      if (SUPPORTS_SCROLLEND) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => settle(el), DEBOUNCE_MS);
    },
    [options, setIndex, settle, valueFromScroll]
  );

  return onScroll;
}

export default function TimeWheelInline({
  value = "",
  onSelect = () => {},
  minuteStep = 15,
  use12h = false,
  className = "",
  columnClassName = "",
  size = "lg",
}) {
  const ITEM_HEIGHT = (COLUMN_SIZE_CONFIG[size] ?? COLUMN_SIZE_CONFIG.lg).itemHeight;
  const hourColRef = useRef(null);
  const minuteColRef = useRef(null);
  const ampmColRef = useRef(null);

  const parsed = useMemo(() => parseTime(value), [value]);
  const initial = parsed ?? { h24: 9, m: 0 };

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, minuteStep);
    const arr = [];
    for (let m = 0; m < 60; m += step) arr.push(m);
    if (60 % step !== 0 && arr[arr.length - 1] !== 59) arr.push(59);
    return arr;
  }, [minuteStep]);

  const hourOptions = useMemo(() => {
    return use12h
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : Array.from({ length: 24 }, (_, i) => i);
  }, [use12h]);

  const ampmOptions = useMemo(() => ["AM", "PM"], []);

  const [selHourIdx, setSelHourIdx] = useState(() => {
    const h24 = initial.h24;
    if (use12h) {
      let hr12 = h24 % 12;
      if (hr12 === 0) hr12 = 12;
      const i = hourOptions.indexOf(hr12);
      return i >= 0 ? i : 0;
    }
    const i = hourOptions.indexOf(h24);
    return i >= 0 ? i : 0;
  });
  const [selMinuteIdx, setSelMinuteIdx] = useState(() => {
    const m = findNearest(minuteOptions, initial.m);
    const i = minuteOptions.indexOf(m);
    return i >= 0 ? i : 0;
  });
  const [selAmpmIdx, setSelAmpmIdx] = useState(() => {
    if (!use12h) return 0;
    return initial.h24 >= 12 ? 1 : 0;
  });

  const selHourIdxRef = useRef(selHourIdx);
  const selMinuteIdxRef = useRef(selMinuteIdx);
  const selAmpmIdxRef = useRef(selAmpmIdx);
  useEffect(() => {
    selHourIdxRef.current = selHourIdx;
  }, [selHourIdx]);
  useEffect(() => {
    selMinuteIdxRef.current = selMinuteIdx;
  }, [selMinuteIdx]);
  useEffect(() => {
    selAmpmIdxRef.current = selAmpmIdx;
  }, [selAmpmIdx]);

  const pad2 = (n) => String(n).padStart(2, "0");

  const indexFromValue = useCallback((v, list) => {
    const i = list.indexOf(v);
    return i >= 0 ? i : 0;
  }, []);

  const valueFromScroll = useCallback((el, list) => {
    if (!el) return { idx: 0, val: list[0] };
    const rawIdx = Math.round(el.scrollTop / ITEM_HEIGHT);
    const idx = Math.max(0, Math.min(list.length - 1, rawIdx));
    return { idx, val: list[idx] };
  }, []);

  const scrollToIndex = useCallback((el, idx, instant = false) => {
    if (!el) return;
    el.scrollTo({ top: idx * ITEM_HEIGHT, behavior: instant ? "instant" : "smooth" });
  }, []);

  const settleToIndex = useCallback(
    (el, idx) => {
      if (!el) return;
      const targetTop = idx * ITEM_HEIGHT;
      if (Math.abs(el.scrollTop - targetTop) > 1) {
        scrollToIndex(el, idx);
      }
    },
    [scrollToIndex]
  );

  const commitSelection = useCallback(
    (hIdx, mIdx, aIdx) => {
      const hourVal = hourOptions[hIdx] ?? hourOptions[0];
      const minuteVal = minuteOptions[mIdx] ?? minuteOptions[0];

      let h24 = hourVal;
      if (use12h) {
        const mer = ampmOptions[aIdx] ?? "AM";
        const isPM = mer === "PM";
        h24 = (hourVal % 12) + (isPM ? 12 : 0);
      }
      onSelect(`${pad2(h24)}:${pad2(minuteVal)}`);
    },
    [hourOptions, minuteOptions, use12h, ampmOptions, onSelect]
  );

  useEffect(() => {
    let h24 = initial.h24;
    let m = findNearest(minuteOptions, initial.m);

    if (use12h) {
      const isPM = h24 >= 12;
      let hr12 = h24 % 12;
      if (hr12 === 0) hr12 = 12;
      const hi = indexFromValue(hr12, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      const ai = isPM ? 1 : 0;
      setSelHourIdx(hi);
      setSelMinuteIdx(mi);
      setSelAmpmIdx(ai);
      setTimeout(() => {
        scrollToIndex(hourColRef.current, hi, true);
        scrollToIndex(minuteColRef.current, mi, true);
        scrollToIndex(ampmColRef.current, ai, true);
      }, 0);
    } else {
      const hi = indexFromValue(h24, hourOptions);
      const mi = indexFromValue(m, minuteOptions);
      setSelHourIdx(hi);
      setSelMinuteIdx(mi);
      setTimeout(() => {
        scrollToIndex(hourColRef.current, hi, true);
        scrollToIndex(minuteColRef.current, mi, true);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commitHour = useCallback(
    (finalIdx) => commitSelection(finalIdx, selMinuteIdxRef.current, selAmpmIdxRef.current),
    [commitSelection]
  );
  const commitMinute = useCallback(
    (finalIdx) => commitSelection(selHourIdxRef.current, finalIdx, selAmpmIdxRef.current),
    [commitSelection]
  );
  const commitAmpm = useCallback(
    (finalIdx) => commitSelection(selHourIdxRef.current, selMinuteIdxRef.current, finalIdx),
    [commitSelection]
  );

  const onScrollHours = useWheelScrollController({
    colRef: hourColRef,
    options: hourOptions,
    setIndex: setSelHourIdx,
    commit: commitHour,
    valueFromScroll,
    settleToIndex,
  });

  const onScrollMinutes = useWheelScrollController({
    colRef: minuteColRef,
    options: minuteOptions,
    setIndex: setSelMinuteIdx,
    commit: commitMinute,
    valueFromScroll,
    settleToIndex,
  });

  const onScrollAmpm = useWheelScrollController({
    colRef: ampmColRef,
    options: ampmOptions,
    setIndex: setSelAmpmIdx,
    commit: commitAmpm,
    valueFromScroll,
    settleToIndex,
  });

  const stepHour = (dir) => {
    const newIdx = Math.max(0, Math.min(hourOptions.length - 1, selHourIdx + dir));
    setSelHourIdx(newIdx);
    scrollToIndex(hourColRef.current, newIdx);
    commitSelection(newIdx, selMinuteIdxRef.current, selAmpmIdxRef.current);
  };

  const stepMinute = (dir) => {
    const newIdx = Math.max(0, Math.min(minuteOptions.length - 1, selMinuteIdx + dir));
    setSelMinuteIdx(newIdx);
    scrollToIndex(minuteColRef.current, newIdx);
    commitSelection(selHourIdxRef.current, newIdx, selAmpmIdxRef.current);
  };

  const stepAmpm = (dir) => {
    const newIdx = Math.max(0, Math.min(ampmOptions.length - 1, selAmpmIdx + dir));
    setSelAmpmIdx(newIdx);
    scrollToIndex(ampmColRef.current, newIdx);
    commitSelection(selHourIdxRef.current, selMinuteIdxRef.current, newIdx);
  };

  return (
    <div className={["flex items-center gap-1.5", className].join(" ")}>
      <TimeWheelColumn
        items={hourOptions}
        selectedIndex={selHourIdx}
        scrollRef={hourColRef}
        onScroll={onScrollHours}
        onStep={stepHour}
        format={(v) => (use12h ? v : pad2(v))}
        className={columnClassName}
        size={size}
      />
      <span className="text-muted font-medium">:</span>
      <TimeWheelColumn
        items={minuteOptions}
        selectedIndex={selMinuteIdx}
        scrollRef={minuteColRef}
        onScroll={onScrollMinutes}
        onStep={stepMinute}
        format={(v) => pad2(v)}
        className={columnClassName}
        size={size}
      />
      {use12h && (
        <TimeWheelColumn
          items={ampmOptions}
          selectedIndex={selAmpmIdx}
          scrollRef={ampmColRef}
          onScroll={onScrollAmpm}
          onStep={stepAmpm}
          format={(v) => v}
          size={size}
        />
      )}
    </div>
  );
}

/* ---------- utils ---------- */
function findNearest(list, value) {
  let best = list[0],
    diff = Infinity;
  for (const v of list) {
    const d = Math.abs(v - value);
    if (d < diff) {
      best = v;
      diff = d;
    }
  }
  return best;
}

function parseTime(str) {
  if (!str || typeof str !== "string") return null;
  const m24 = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(str.trim());
  if (m24) return { h24: +m24[1], m: +m24[2] };
  const m12 = /^([1-9]|1[0-2]):([0-5]\d)\s*([AP]M)$/i.exec(str.trim());
  if (m12) {
    let h = +m12[1],
      m = +m12[2];
    const mer = m12[3].toUpperCase();
    if (mer === "PM" && h !== 12) h += 12;
    if (mer === "AM" && h === 12) h = 0;
    return { h24: h, m };
  }
  return null;
}